import { useNode } from "@craftjs/core";
import React from "react";
import { isUpdatedFromDefault } from "../../utils";

import {
  ColorInput,
  Panel,
  Tabs,
  TextInput,
  Spacer,
  AlignInput,
  RangeNumberInput,
  SelectInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const StarRatingSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    totalStars = 5,
    rating = 3,
    iconSize = 20,
    iconSpacing,
    align,
    suffix,
    prefix,
    activeColor = "#ffa500",
    inactiveColor = "#000000",
    verticalAlign,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <RangeNumberInput
            resetValue={5}
            label="Total Stars"
            value={totalStars}
            onChange={(val) => {
              setProp((props) => {
                if (val < rating) {
                  props.rating = Number(val);
                }
                props.totalStars = Number(val);
                return props;
              });
            }}
          />
          <RangeNumberInput
            resetValue={3}
            max={totalStars}
            label="Rating"
            value={rating}
            onChange={(val) => {
              setProp((props) => {
                props.rating = Number(val);
              });
            }}
          />
          <RangeNumberInput
            resetValue={20}
            unit="px"
            label="Icon Size"
            value={iconSize}
            onChange={(val) => {
              setProp((props) => {
                props.iconSize = val;
              });
            }}
          />
          <RangeNumberInput
            resetValue={undefined}
            label="Icon Spacing"
            value={iconSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.iconSpacing = val;
              });
            }}
          />

          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: prefix },
            { key: suffix },
            { key: verticalAlign },
          ])}
          id={`block-setting-${id}-text`}
          title={"Text"}
          initialOpen={true}
        >
          <TextInput
            minLength={INPUT_LIMITS["star-rating-prefix"].minlength}
            maxLength={INPUT_LIMITS["star-rating-prefix"].maxlength}
            resetValue={undefined}
            label="Prefix"
            value={prefix}
            onChange={(val) => {
              setProp((props) => {
                props.prefix = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["star-rating-suffix"].minlength}
            maxLength={INPUT_LIMITS["star-rating-suffix"].maxlength}
            resetValue={undefined}
            label="Suffix"
            value={suffix}
            onChange={(val) => {
              setProp((props) => {
                props.suffix = val;
              });
            }}
          />
          <SelectInput
            resetValue={undefined}
            label="Label Alignment"
            value={verticalAlign}
            onChange={(val) => {
              setProp((props) => {
                props.verticalAlign = val;
              });
            }}
            options={[
              {
                label: "Top",
                value: "start",
              },
              {
                label: "Center",
                value: "center",
              },
              {
                label: "Bottom",
                value: "end",
              },
            ]}
          />
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: activeColor, default: "#ffa500" },
              { key: inactiveColor, default: "#000000" },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <ColorInput
              resetValue={"#ffa500"}
              value={activeColor}
              onChange={(val) => {
                setProp((props) => {
                  props.activeColor = val;
                });
              }}
              label={"Active"}
            />
            <ColorInput
              resetValue={"#000000"}
              value={inactiveColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inactiveColor = val;
                });
              }}
              label={"Inactive"}
            />
          </Panel>
        </div>
      </>
    );
  }
  function AdvancedSettings() {
    return (
      <>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }

  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: totalStars },
        { key: rating },
        { key: iconSize },
        { key: iconSpacing },
        { key: align },
        { key: prefix },
        { key: suffix },
        { key: verticalAlign },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: activeColor, default: "#ffa500" },
        { key: inactiveColor, default: "#000000" },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
